import { useCallback, useEffect, useRef, useState } from "react";
import { listInventoryItems, type InventoryListItem } from "../../../api/catalog";
import { Button } from "../../primitives/Button";
import { InventoryArticleDetail } from "./InventoryArticleDetail";
import { cn } from "../../utils/cn";

const STATUS_FILTER_OPTIONS = [
  { value: "", label: "Tous les statuts" },
  { value: "active", label: "Actif" },
  { value: "inactive", label: "Inactif" },
  { value: "archived", label: "Archivé" },
];

const STOCK_STATUS_OPTIONS = [
  { value: "", label: "Tous" },
  { value: "rupture", label: "Rupture" },
  { value: "faible", label: "Faible" },
  { value: "ok", label: "OK" },
];

const COLUMNS = [
  { key: "code", label: "Code", show: true },
  { key: "nom", label: "Nom", show: true },
  { key: "categorie", label: "Catégorie", show: true },
  { key: "type", label: "Type", show: true },
  { key: "disponible", label: "Disponible", show: true },
  { key: "reserve", label: "Réservé", show: true },
  { key: "en_commande", label: "En commande", show: true },
  { key: "seuil", label: "Seuil", show: true },
  { key: "statut", label: "Statut", show: true },
  { key: "fournisseur", label: "Fournisseur principal", show: true },
  { key: "actions", label: "Action", show: true },
] as const;

type InventoryArticlesProps = { selectedArticleId?: string | null };

const PAGE_SIZE = 30;

export function InventoryArticles({ selectedArticleId = null }: InventoryArticlesProps) {
  const [items, setItems] = useState<InventoryListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [stockStatusFilter, setStockStatusFilter] = useState("");
  const [groupByCategory, setGroupByCategory] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState(COLUMNS.map((c) => ({ ...c })));
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const columnPickerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showColumnPicker) return;
    const onDocClick = (e: MouseEvent) => {
      if (columnPickerRef.current && !columnPickerRef.current.contains(e.target as Node)) setShowColumnPicker(false);
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [showColumnPicker]);

  const toggleColumn = (key: (typeof COLUMNS)[number]["key"]) => {
    setColumnVisibility((prev) =>
      prev.map((c) => (c.key === key ? { ...c, show: !c.show } : c))
    );
  };

  // Initial load + reset when filters change
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setItems([]);
    setPage(1);
    setHasMore(true);
    listInventoryItems({
      query: searchQuery || undefined,
      status: statusFilter || undefined,
      page: 1,
      page_size: PAGE_SIZE,
    })
      .then((res) => {
        if (!cancelled) {
          let list = res.items;
          if (stockStatusFilter) list = list.filter((i) => i.stock_status === stockStatusFilter);
          setItems(list);
          setTotal(res.meta.total);
          setHasMore(list.length === PAGE_SIZE && res.meta.total > list.length);
        }
      })
      .catch((err: Error & { status?: number }) => {
        if (!cancelled) {
          setItems([]);
          const msg = err?.message || "Impossible de charger l'inventaire.";
          setError(err?.status === 403 ? "Accès refusé au module Catalogue. Vérifiez votre organisation (XLKey)." : msg);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [searchQuery, statusFilter, stockStatusFilter]);

  // Load more (infinite scroll)
  const loadMore = useCallback(() => {
    if (!hasMore || loadingMore || loading) return;
    const nextPage = page + 1;
    setLoadingMore(true);
    listInventoryItems({
      query: searchQuery || undefined,
      status: statusFilter || undefined,
      page: nextPage,
      page_size: PAGE_SIZE,
    })
      .then((res) => {
        let list = res.items;
        if (stockStatusFilter) list = list.filter((i) => i.stock_status === stockStatusFilter);
        setItems((prev) => [...prev, ...list]);
        setTotal(res.meta.total);
        setPage(nextPage);
        setHasMore(list.length === PAGE_SIZE);
      })
      .finally(() => setLoadingMore(false));
  }, [hasMore, loadingMore, loading, page, searchQuery, statusFilter, stockStatusFilter]);

  // IntersectionObserver: when sentinel is visible, load more
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { root: scrollContainerRef.current, rootMargin: "200px", threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  const displayItems = groupByCategory
    ? [...items].sort((a, b) => (a.product_type || "").localeCompare(b.product_type || ""))
    : items;

  const openDetail = (productId: string) => {
    window.location.hash = `#/inventories/inventaire/${productId}`;
  };
  const closePanel = () => {
    window.location.hash = "#/inventories/inventaire";
  };

  return (
    <div className={cn("flex min-h-0 flex-1", selectedArticleId ? "overflow-hidden" : "")}>
      <div className={cn("flex flex-col min-w-0 p-6", selectedArticleId ? "w-1/2 lg:w-2/3" : "flex-1")}>
        <div className="flex items-center justify-between gap-4 mb-4">
          <h1 className="text-xl font-semibold text-text">Inventaire</h1>
        </div>

        {/* Barre de filtres — design compact et professionnel */}
        <div className="rounded-lg border border-border bg-surface-muted/40 px-4 py-3 mb-4">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
            <div className="min-w-[180px] max-w-[240px]">
              <label className="block text-xs font-medium text-muted mb-1">Recherche</label>
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                placeholder="Nom ou code..."
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>
            <div className="w-[140px]">
              <label className="block text-xs font-medium text-muted mb-1">Statut produit</label>
              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                {STATUS_FILTER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div className="w-[120px]">
              <label className="block text-xs font-medium text-muted mb-1">Stock</label>
              <select
                value={stockStatusFilter}
                onChange={(e) => { setStockStatusFilter(e.target.value); setPage(1); }}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                {STOCK_STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2 pt-5">
              <label className="inline-flex items-center gap-2 text-sm text-text cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={groupByCategory}
                  onChange={(e) => setGroupByCategory(e.target.checked)}
                  className="h-4 w-4 rounded border-border text-primary focus:ring-primary/20"
                />
                Grouper par catégorie
              </label>
            </div>
            <div className="relative ml-auto pt-5" ref={columnPickerRef}>
              <Button
                label="Colonnes affichées"
                intent="default"
                size="sm"
                onClick={() => setShowColumnPicker((v) => !v)}
              />
              {showColumnPicker && (
                <div className="absolute right-0 top-full mt-1 z-20 rounded-lg border border-border bg-surface shadow-lg py-2 min-w-[180px]">
                  <div className="px-3 py-1.5 text-xs font-medium text-muted border-b border-border mb-1">Afficher</div>
                  {columnVisibility.map((c) => (
                    <label key={c.key} className="flex items-center gap-2 px-3 py-2 text-sm text-text cursor-pointer hover:bg-surface-muted">
                      <input
                        type="checkbox"
                        checked={c.show}
                        onChange={() => toggleColumn(c.key)}
                        className="h-4 w-4 rounded border-border text-primary focus:ring-primary/20"
                      />
                      {c.label}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {error ? (
          <div className="rounded-md border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-danger">
            {error}
          </div>
        ) : loading ? (
          <p className="text-sm text-muted">Chargement...</p>
        ) : (
          <div className="flex flex-col min-h-0 flex-1 min-w-0">
            <div
              ref={scrollContainerRef}
              className="flex-1 min-h-0 overflow-y-auto overflow-x-auto rounded-md border border-border"
            >
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-surface-muted">
                    {columnVisibility.find((c) => c.key === "code")?.show && <th className="text-left p-3 font-medium text-text">Code</th>}
                    {columnVisibility.find((c) => c.key === "nom")?.show && <th className="text-left p-3 font-medium text-text">Nom</th>}
                    {columnVisibility.find((c) => c.key === "categorie")?.show && <th className="text-left p-3 font-medium text-text">Catégorie</th>}
                    {columnVisibility.find((c) => c.key === "type")?.show && <th className="text-left p-3 font-medium text-text">Type</th>}
                    {columnVisibility.find((c) => c.key === "disponible")?.show && <th className="text-right p-3 font-medium text-text">Disponible</th>}
                    {columnVisibility.find((c) => c.key === "reserve")?.show && <th className="text-right p-3 font-medium text-text">Réservé</th>}
                    {columnVisibility.find((c) => c.key === "en_commande")?.show && <th className="text-right p-3 font-medium text-text">En commande</th>}
                    {columnVisibility.find((c) => c.key === "seuil")?.show && <th className="text-right p-3 font-medium text-text">Seuil</th>}
                    {columnVisibility.find((c) => c.key === "statut")?.show && <th className="text-center p-3 font-medium text-text">Statut</th>}
                    {columnVisibility.find((c) => c.key === "fournisseur")?.show && <th className="text-left p-3 font-medium text-text">Fournisseur principal</th>}
                    {columnVisibility.find((c) => c.key === "actions")?.show && <th className="text-right p-3 font-medium text-text">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {displayItems.length === 0 ? (
                    <tr>
                      <td colSpan={columnVisibility.filter((c) => c.show).length || 11} className="p-4 text-center text-muted">
                        Aucun article avec suivi d'inventaire. Les produits de type Matériel avec suivi d'inventaire activé (Catalogue) apparaissent ici.
                      </td>
                    </tr>
                  ) : (
                    displayItems.map((row) => (
                      <tr
                        key={row.product_id}
                        className={cn(
                          "border-b border-border hover:bg-surface-muted/50 cursor-pointer",
                          selectedArticleId === row.product_id && "bg-primary/5"
                        )}
                        onClick={() => openDetail(row.product_id)}
                      >
                        {columnVisibility.find((c) => c.key === "code")?.show && <td className="p-3 text-muted font-mono text-xs">{row.product_code ?? "—"}</td>}
                        {columnVisibility.find((c) => c.key === "nom")?.show && <td className="p-3 font-medium text-text">{row.product_name}</td>}
                        {columnVisibility.find((c) => c.key === "categorie")?.show && (
                          <td className="p-3 text-muted text-sm">{(row.category_labels ?? []).join(", ") || "—"}</td>
                        )}
                        {columnVisibility.find((c) => c.key === "type")?.show && (
                          <td className="p-3 text-muted text-sm">{row.product_type ?? "—"}</td>
                        )}
                        {columnVisibility.find((c) => c.key === "disponible")?.show && <td className="text-right p-3 font-medium tabular-nums">{row.available_quantity}</td>}
                        {columnVisibility.find((c) => c.key === "reserve")?.show && <td className="text-right p-3 tabular-nums">{row.reserved_quantity}</td>}
                        {columnVisibility.find((c) => c.key === "en_commande")?.show && <td className="text-right p-3 tabular-nums">{row.quantity_on_order}</td>}
                        {columnVisibility.find((c) => c.key === "seuil")?.show && <td className="text-right p-3 tabular-nums">{row.low_stock_threshold ?? "—"}</td>}
                        {columnVisibility.find((c) => c.key === "statut")?.show && (
                          <td className="text-center p-3">
                            <span
                              className={cn(
                                "rounded px-2 py-0.5 text-xs font-medium",
                                row.stock_status === "rupture" && "bg-danger/15 text-danger",
                                row.stock_status === "faible" && "bg-warning/15 text-warning",
                                row.stock_status === "ok" && "bg-success/15 text-success"
                              )}
                            >
                              {row.stock_status === "rupture" ? "Rupture" : row.stock_status === "faible" ? "Faible" : "OK"}
                            </span>
                          </td>
                        )}
                        {columnVisibility.find((c) => c.key === "fournisseur")?.show && (
                          <td className="p-3 text-muted text-sm">{row.default_supplier_name ?? "—"}</td>
                        )}
                        {columnVisibility.find((c) => c.key === "actions")?.show && (
                          <td className="text-right p-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                            <a href={`#/inventories/inventaire/${row.product_id}`} className="text-primary hover:underline text-xs">
                              Détails
                            </a>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              <div ref={sentinelRef} className="h-4 flex-shrink-0" aria-hidden />
              {loadingMore && (
                <div className="py-3 text-center text-sm text-muted">Chargement...</div>
              )}
            </div>

            <div className="flex items-center justify-between text-sm text-muted mt-2 flex-shrink-0">
              <span>
                {items.length} article(s) chargés
                {total > items.length && ` sur ${total}`}
              </span>
            </div>
          </div>
        )}
      </div>

      {selectedArticleId && (
        <div className="w-full lg:w-1/3 xl:w-[420px] border-l border-border bg-surface flex flex-col overflow-hidden shrink-0">
          <div className="flex-1 overflow-y-auto min-h-0">
            <InventoryArticleDetail
              productId={selectedArticleId}
              onBack={closePanel}
            />
          </div>
        </div>
      )}
    </div>
  );
}
